import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, Form, FormFeedback, Input, Label, Row, Col, Button } from 'reactstrap';
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import Select from 'react-select';

const eventTypeOptions = [
  { value: 'Rapor', label: 'Rapor' },
  { value: 'Bakım', label: 'Bakım' },
  { value: 'Onarım', label: 'Onarım' },
  { value: 'Teklif', label: 'Teklif' },
  { value: 'Hatırlatma', label: 'Hatırlatma' },
  { value: 'Proje Başlangıcı', label: 'Proje Başlangıcı' },
  { value: 'Proje Bitişi', label: 'Proje Bitişi' },
  { value: 'Fatura', label: 'Fatura' },
  { value: 'Tedarikçi', label: 'Tedarikçi' },
  { value: 'Teknik Servis', label: 'Teknik Servis' },
];

const EditEventModal = ({ isOpen, toggle, selectedEvent, onEventUpdated, pb }) => {
  const [relatedData, setRelatedData] = useState({
    customers: [],
    services: [],
    products: [],
    invoices: [],
  });
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch related data without using AbortController
  const fetchRelatedData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [customerRecords, serviceRecords, productRecords, invoiceRecords] = await Promise.all([
        pb.collection('customers').getFullList({ $autoCancel: false }),
        pb.collection('services').getFullList({ $autoCancel: false }),
        pb.collection('products').getFullList({ $autoCancel: false }),
        pb.collection('invoices').getFullList({ $autoCancel: false }),
      ]);

      setRelatedData({
        customers: customerRecords.map(customer => ({ value: customer.id, label: customer.name })),
        services: serviceRecords.map(service => ({ value: service.id, label: service.name || service.title || "İsimsiz Hizmet" })),
        products: productRecords.map(product => ({ value: product.id, label: product.product_name })),
        invoices: invoiceRecords.map(invoice => ({ value: invoice.id, label: invoice.title || invoice.name || "İsimsiz Teklif" })),
      });
    } catch (error) {
      // Only log errors that are not related to request cancellation
      if (!(error.name === "ClientResponseError" && error.status === 0)) {
        console.error("Error fetching related data:", error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [pb]);

  useEffect(() => {
    fetchRelatedData();
    // No need for a cleanup function as we're not using AbortController anymore
  }, [fetchRelatedData]);

  const validationSchema = Yup.object({
    title: Yup.string().required("Etkinlik adı zorunludur"),
    event_type: Yup.array().min(1, "En az bir etkinlik tipi seçilmelidir"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      event_type: [],
      description: "",
      location: "",
      status: "Planlandı",
      customer_id: [],
      service_id: [],
      product_id: [],
      invoice_id: [],
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const eventData = {
          ...values,
          start_date: dateRange[0].toISOString(),
          end_date: dateRange[1].toISOString(),
          event_type: values.event_type.map(type => type.value),
          customer_id: values.customer_id.map(customer => customer.value),
          service_id: values.service_id.map(service => service.value),
          product_id: values.product_id.map(product => product.value),
          invoice_id: values.invoice_id.map(invoice => invoice.value),
        };
        await pb.collection('events').update(selectedEvent.id, eventData);
        toggle();
        onEventUpdated();
      } catch (error) {
        console.error("Error updating event:", error);
      }
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (selectedEvent && !isLoading) {
      const eventType = Array.isArray(selectedEvent.event_type)
        ? selectedEvent.event_type.map(type => eventTypeOptions.find(et => et.value === type) || { value: type, label: type })
        : [];

      formik.setValues({
        title: selectedEvent.title || "",
        event_type: eventType,
        description: selectedEvent.description || "",
        location: selectedEvent.location || "",
        status: selectedEvent.status || "Planlandı",
        customer_id: selectedEvent.customer_id ? selectedEvent.customer_id.map(id => relatedData.customers.find(c => c.value === id)).filter(Boolean) : [],
        service_id: selectedEvent.service_id ? selectedEvent.service_id.map(id => relatedData.services.find(s => s.value === id)).filter(Boolean) : [],
        product_id: selectedEvent.product_id ? selectedEvent.product_id.map(id => relatedData.products.find(p => p.value === id)).filter(Boolean) : [],
        invoice_id: selectedEvent.invoice_id ? selectedEvent.invoice_id.map(id => relatedData.invoices.find(i => i.value === id)).filter(Boolean) : [],
      });
      setDateRange([new Date(selectedEvent.start), new Date(selectedEvent.end)]);
    }
  }, [selectedEvent, relatedData, isLoading]);

  const handleDeleteEvent = async () => {
    if (selectedEvent && selectedEvent.id) {
      try {
        await pb.collection('events').delete(selectedEvent.id);
        toggle();
        onEventUpdated();
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle} tag="h5" className="p-3 bg-info-subtle modal-title">
        Etkinliği Düzenle
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Etkinlik Adı</Label>
                <Input
                  name="title"
                  type="text"
                  {...formik.getFieldProps('title')}
                  invalid={formik.touched.title && formik.errors.title}
                />
                {formik.touched.title && formik.errors.title && (
                  <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Etkinlik Tipi</Label>
                <Select
                  isMulti
                  name="event_type"
                  options={eventTypeOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.event_type}
                  onChange={(selectedOptions) => formik.setFieldValue('event_type', selectedOptions)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Label>Etkinlik Tarihi</Label>
                <Flatpickr
                  className="form-control"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d H:i",
                    enableTime: true,
                    time_24hr: true,
                  }}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Açıklama</Label>
                <Input
                  type="textarea"
                  rows="3"
                  {...formik.getFieldProps('description')}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Konum</Label>
                <Input
                  name="location"
                  type="text"
                  {...formik.getFieldProps('location')}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Durum</Label>
                <Input
                  type="select"
                  name="status"
                  {...formik.getFieldProps('status')}
                >
                  <option value="Planlandı">Planlandı</option>
                  <option value="Tamamlandı">Tamamlandı</option>
                  <option value="İptal Edildi">İptal Edildi</option>
                </Input>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Müşteriler</Label>
                <Select
                  isMulti
                  name="customer_id"
                  options={relatedData.customers}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.customer_id}
                  onChange={(selected) => formik.setFieldValue('customer_id', selected)}
                  placeholder="Seçiniz..."
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Hizmetler</Label>
                <Select
                  isMulti
                  name="service_id"
                  options={relatedData.services}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.service_id}
                  onChange={(selected) => formik.setFieldValue('service_id', selected)}
                  placeholder="Seçiniz..."
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Ürünler</Label>
                <Select
                  isMulti
                  name="product_id"
                  options={relatedData.products}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.product_id}
                  onChange={(selected) => formik.setFieldValue('product_id', selected)}
                  placeholder="Seçiniz..."
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Teklifler</Label>
                <Select
                  isMulti
                  name="invoice_id"
                  options={relatedData.invoices}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={formik.values.invoice_id}
                  onChange={(selected) => formik.setFieldValue('invoice_id', selected)}
                  placeholder="Seçiniz..."
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="text-end">
              <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                Değişiklikleri Kaydet
              </Button>
              <Button type="button" className="ms-2" color="danger" onClick={handleDeleteEvent} disabled={formik.isSubmitting}>
                Sil
              </Button>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default EditEventModal;
